<template>
  <div class="main-content page-inprogress-view">
    <div class="card page-card">
      <div class="top-actions">
        <button class="btn btn-light btn-sm" @click="goBack">
          <i class="fa fa-arrow-left me-1"></i> Voltar
        </button>
      </div>

      <div v-if="loading" class="empty-state">Carregando andamento...</div>

      <div v-else-if="loadError" class="empty-state error-state">
        {{ loadError }}
      </div>

      <div v-else-if="!flow || !flow.id" class="empty-state">
        Fluxo não encontrado.
      </div>

      <template v-else>
        <div class="summary-card">
          <div class="summary-title-block">
            <div class="summary-label">Fluxo</div>
            <h2 class="summary-title">{{ flowTitle(flow) }}</h2>
            <div class="summary-tag">{{ flowCode(flow) }}</div>
          </div>

          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-item-label">Status geral</span>
              <span
                class="status-badge"
                :class="statusClass(displayStatus(flow))"
              >
                {{ statusLabel(displayStatus(flow)) }}
              </span>
            </div>

            <div class="summary-item">
              <span class="summary-item-label">Etapa atual</span>
              <strong>{{ currentStepText }}</strong>
              <div class="summary-mini">{{ currentStepName }}</div>
            </div>

            <div class="summary-item">
              <span class="summary-item-label">Solicitante</span>
              <strong>{{ openedByName(flow) }}</strong>
            </div>

            <div class="summary-item">
              <span class="summary-item-label">Área dona</span>
              <strong>{{ ownerDepartmentName(flow) }}</strong>
            </div>

            <div class="summary-item">
              <span class="summary-item-label">Abertura</span>
              <strong>{{ formatDate(flowCreatedAt(flow)) }}</strong>
            </div>

            <div v-if="flow.status === 'cancelled'" class="summary-item">
              <span class="summary-item-label">Cancelado por</span>
              <strong>{{
                flow.cancelled_by_name ||
                (flow.cancelledBy && flow.cancelledBy.name) ||
                "—"
              }}</strong>
              <div class="summary-mini">
                {{ formatDate(flow.cancelled_at) }}
              </div>
            </div>
          </div>
        </div>

        <div class="section-block">
          <div class="section-header">
            <h4>Etapas do fluxo</h4>
            <span class="section-subtitle">
              Acompanhe o progresso e quem finalizou cada etapa.
            </span>
          </div>

          <div v-if="stepsView.length" class="steps-grid">
            <div
              v-for="step in stepsView"
              :key="String(step.step_order) + '-' + String(step.id || '')"
              class="step-card"
              :class="step.visual_status"
            >
              <div class="step-card-top">
                <span class="step-order">Etapa {{ step.step_order }}</span>
                <span class="step-badge" :class="step.visual_status">
                  {{ step.visual_label }}
                </span>
              </div>

              <div class="step-title">{{ step.name }}</div>
              <div class="step-department">{{ step.department_name }}</div>

              <div class="step-info-list">
                <div class="step-info-item">
                  <span class="label">Responsável</span>
                  <strong>{{ step.approver_name || "—" }}</strong>
                </div>

                <div class="step-info-item">
                  <span class="label">Finalizado por</span>
                  <strong>{{ step.finished_by || "—" }}</strong>
                </div>

                <div class="step-info-item">
                  <span class="label">Data</span>
                  <strong>{{ formatDate(step.finished_at) }}</strong>
                </div>
              </div>

              <div class="step-description">
                <span class="label">Descrições</span>

                <div
                  v-if="step.descriptions && step.descriptions.length"
                  class="description-list"
                >
                  <div
                    v-for="item in step.descriptions"
                    :key="item.id"
                    class="description-item"
                  >
                    <div class="description-meta">
                      <strong>{{ item.user_name }}</strong>
                      <span>{{ formatDate(item.created_at) }}</span>
                    </div>

                    <p>{{ item.text }}</p>
                  </div>
                </div>

                <p v-else>Sem descrição nesta etapa.</p>
              </div>

              <div
                v-if="step.approvals && step.approvals.length"
                class="step-approvals"
              >
                <span class="label">Aprovações</span>

                <ul class="approval-list">
                  <li
                    v-for="approval in step.approvals"
                    :key="approval.id"
                    class="approval-item"
                  >
                    <div class="approval-top">
                      <strong>{{ approval.approver_name }}</strong>

                      <span
                        class="approval-status"
                        :class="approvalStatusClass(approval.status)"
                      >
                        {{ approvalStatusLabel(approval.status) }}
                      </span>
                    </div>

                    <div class="approval-date">
                      {{ formatDate(approval.created_at) }}
                    </div>

                    <div v-if="approval.comment" class="approval-comment">
                      {{ approval.comment }}
                    </div>
                  </li>
                </ul>
              </div>

              <div v-if="step.files && step.files.length" class="step-files">
                <span class="label">Anexos</span>
                <ul>
                  <li v-for="file in step.files" :key="file.id">
                    {{ file.original_name }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            Nenhuma etapa encontrada para este fluxo.
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "InprogressView",

  data() {
    return {
      loading: false,
      loadError: "",
      flow: null,
      historyByStep: [],
    };
  },

  computed: {
    api() {
      return this.$store && this.$store.state ? this.$store.state.api : "";
    },

    currentStepText() {
      var transitions = this.flowTransitions(this.flow);
      var total = transitions.length;
      var current = this.currentStepValue(this.flow);

      if (!total) return "—";
      return current + " de " + total;
    },

    currentStepName() {
      var current = this.currentStepValue(this.flow);
      var transitions = this.flowTransitions(this.flow);

      var step = transitions.find(function (item) {
        return Number(item.step_order) === Number(current);
      });

      return step && step.name ? step.name : "—";
    },

    stepsView() {
      var flow = this.flow || {};
      var transitions = this.flowTransitions(flow)
        .slice()
        .sort(function (a, b) {
          return Number(a.step_order || 0) - Number(b.step_order || 0);
        });

      return transitions.map(
        function (step) {
          var latestHistory = this.stepCompletedMeta(step);

          var files = this.flowFiles(flow).filter(
            function (file) {
              return Number(this.fileTransitionId(file)) === Number(step.id);
            }.bind(this)
          );

          var approvals = this.flowApprovals(flow)
            .filter(
              function (item) {
                return (
                  Number(this.approvalTransitionId(item)) === Number(step.id)
                );
              }.bind(this)
            )
            .sort(function (a, b) {
              return (
                new Date(b.created_at || b.createdAt || 0) -
                new Date(a.created_at || a.createdAt || 0)
              );
            });

          var latestApproval = approvals.length ? approvals[0] : null;
          var visual = this.getStepVisualStatus(step, latestApproval);

          return {
            id: step.id || null,
            step_order: step.step_order || "—",
            name: step.name || "Etapa " + (step.step_order || ""),
            department_name: this.stepDepartmentName(step),
            approver_name: this.openedByName(flow),

            finished_by:
              visual === "done" || visual === "rejected"
                ? (latestApproval &&
                    ((latestApproval.approver &&
                      latestApproval.approver.name) ||
                      latestApproval.approver_name ||
                      (latestApproval.user && latestApproval.user.name))) ||
                  (latestHistory ? latestHistory.user_name || "—" : "—")
                : "—",

            finished_at:
              visual === "done" || visual === "rejected"
                ? (latestApproval &&
                    (latestApproval.created_at ||
                      latestApproval.createdAt ||
                      null)) ||
                  (latestHistory ? latestHistory.created_at || null : null)
                : null,

            descriptions: this.stepDescriptions(step, flow),

            files: files,

            approvals: approvals.map(function (item) {
              return {
                id: item.id,
                status: item.status || "—",
                approver_name:
                  (item.approver && item.approver.name) ||
                  item.approver_name ||
                  (item.user && item.user.name) ||
                  "—",
                created_at: item.created_at || item.createdAt || null,
                comment: item.comment || item.description || "",
              };
            }),

            visual_status: visual,
            visual_label: this.stepVisualLabel(visual),
          };
        }.bind(this)
      );
    },
  },

  mounted() {
    this.loadFlow();
  },

  methods: {
    buildFlowDetailUrls(id) {
      var base = (this.api || "").replace(/\/+$/, "");

      if (!base) {
        return ["/api/v1/flows/" + id, "/api/flows/" + id, "/flows/" + id];
      }

      if (/\/api\/v1$/i.test(base)) {
        return [base + "/flows/" + id];
      }

      if (/\/api$/i.test(base)) {
        return [base + "/v1/flows/" + id, base + "/flows/" + id];
      }

      return [
        base + "/api/v1/flows/" + id,
        base + "/api/flows/" + id,
        base + "/flows/" + id,
      ];
    },

    async loadFlow() {
      this.loading = true;
      this.loadError = "";
      this.flow = null;
      this.historyByStep = [];

      try {
        var id = this.$route.params.id;
        var urls = this.buildFlowDetailUrls(id);
        var i = 0;
        var response = null;

        for (i = 0; i < urls.length; i++) {
          try {
            response = await axios.get(urls[i]);
            this.flow = this.normalizeItemResponse(response.data);
            this.buildHistoryByStep();
            return;
          } catch (innerError) {}
        }

        this.loadError = "Não foi possível carregar o fluxo.";
      } catch (error) {
        this.loadError = "Erro ao carregar fluxo.";
      } finally {
        this.loading = false;
      }
    },

    normalizeItemResponse(data) {
      if (data && data.data && !Array.isArray(data.data)) return data.data;
      if (data && data.result && !Array.isArray(data.result))
        return data.result;
      return data || {};
    },

    goBack() {
      this.$router.push({ name: "list-flow" });
    },

    flowCode(flow) {
      if (flow && flow.tag) return flow.tag;
      return "FL-" + (flow && flow.id ? flow.id : "");
    },

    flowTitle(flow) {
      return (flow && flow.title) || "Sem título";
    },

    flowStatus(flow) {
      return (flow && flow.status) || "new";
    },

    displayStatus(flow) {
      var rawStatus = this.flowStatus(flow);

      if (rawStatus === "cancelled") return "cancelled";
      if (rawStatus === "rejected") return "rejected";
      if (rawStatus === "waiting_approval") return "waiting_approval";
      if (rawStatus === "new") return "new";

      if (rawStatus === "done") {
        if (this.hasRealFinalization(flow)) {
          return "done";
        }
        return "in_progress";
      }

      return rawStatus || "in_progress";
    },

    hasRealFinalization(flow) {
      if (!flow) return false;

      if (
        flow.finished_at ||
        flow.finishedAt ||
        flow.completed_at ||
        flow.completedAt ||
        flow.closed_at ||
        flow.closedAt ||
        flow.finalized_at ||
        flow.finalizedAt
      ) {
        return true;
      }

      if (
        flow.finished_by_user_id ||
        flow.completed_by_user_id ||
        flow.closed_by_user_id ||
        flow.finalized_by_user_id
      ) {
        return true;
      }

      return false;
    },

    flowDescription(flow) {
      return (flow && (flow.description || flow.initial_description)) || "";
    },

    flowCreatedAt(flow) {
      return (flow && (flow.created_at || flow.createdAt)) || null;
    },

    currentStepValue(flow) {
      if (flow && flow.current_step_order)
        return Number(flow.current_step_order);
      if (flow && flow.currentStepOrder) return Number(flow.currentStepOrder);
      return 0;
    },

    openedByName(flow) {
      if (flow && flow.openedBy && flow.openedBy.name)
        return flow.openedBy.name;
      if (flow && flow.opened_by && flow.opened_by.name)
        return flow.opened_by.name;
      if (flow && flow.opened_by_name) return flow.opened_by_name;
      return "—";
    },

    ownerDepartmentName(flow) {
      if (flow && flow.ownerDepartment && flow.ownerDepartment.name)
        return flow.ownerDepartment.name;
      if (flow && flow.owner_department && flow.owner_department.name)
        return flow.owner_department.name;
      if (flow && flow.owner_department_name) return flow.owner_department_name;
      return "—";
    },

    flowTransitions(flow) {
      if (flow && Array.isArray(flow.transitions)) return flow.transitions;
      if (flow && Array.isArray(flow.flow_transitions))
        return flow.flow_transitions;
      return [];
    },

    flowApprovals(flow) {
      if (flow && Array.isArray(flow.approvals)) return flow.approvals;
      if (flow && Array.isArray(flow.flow_approvals))
        return flow.flow_approvals;
      return [];
    },

    flowFiles(flow) {
      if (flow && Array.isArray(flow.files)) return flow.files;
      if (flow && Array.isArray(flow.flow_files)) return flow.flow_files;
      return [];
    },

    approvalTransitionId(item) {
      if (!item) return null;
      return item.transition_id || item.flow_transition_id || null;
    },

    fileTransitionId(file) {
      if (!file) return null;
      return file.transition_id || file.flow_transition_id || null;
    },

    stepDepartmentName(step) {
      if (step && step.department && step.department.name)
        return step.department.name;
      if (step && step.department_name) return step.department_name;
      return "—";
    },

    statusLabel(status) {
      var map = {
        new: "Novo",
        in_progress: "Em andamento",
        waiting_approval: "Aguardando aprovação",
        rejected: "Reprovado",
        done: "Concluído",
        cancelled: "Cancelado",
      };

      return map[status] || status || "—";
    },

    statusClass(status) {
      return {
        "is-new": status === "new",
        "is-progress":
          status === "in_progress" || status === "waiting_approval",
        "is-done": status === "done",
        "is-rejected": status === "rejected" || status === "cancelled",
      };
    },

    approvalStatusLabel(status) {
      var map = {
        pending: "Pendente",
        approved: "Aprovado",
        rejected: "Reprovado",
        waiting_approval: "Aguardando aprovação",
        done: "Concluído",
      };

      return map[status] || this.statusLabel(status);
    },

    approvalStatusClass(status) {
      return {
        "is-approved": status === "approved" || status === "done",
        "is-rejected": status === "rejected",
        "is-pending":
          status === "pending" ||
          status === "waiting_approval" ||
          status === "in_progress",
      };
    },

    getStepVisualStatus(step, latestApproval) {
      var currentStep = this.currentStepValue(this.flow);
      var flowStatus = this.flowStatus(this.flow);
      var isFlowFinished =
        flowStatus === "done" ||
        flowStatus === "rejected" ||
        flowStatus === "cancelled";

      if (latestApproval && latestApproval.status === "rejected")
        return "rejected";
      if (isFlowFinished && Number(step.step_order) <= Number(currentStep))
        return "done";
      if (Number(step.step_order) < Number(currentStep)) return "done";
      if (Number(step.step_order) === Number(currentStep)) return "current";
      return "pending";
    },

    stepVisualLabel(status) {
      var map = {
        done: "Concluída",
        current: "Em andamento",
        pending: "Pendente",
        rejected: "Reprovada",
      };

      return map[status] || "—";
    },

    ensureHistoryStep(stepMap, stepOrder) {
      if (!stepMap[stepOrder]) {
        stepMap[stepOrder] = {
          step_order: stepOrder,
          step_name: this.stepNameByOrder(stepOrder),
          items: [],
        };
      }

      return stepMap[stepOrder];
    },

    stepNameByOrder(stepOrder) {
      var steps = this.flowTransitions(this.flow);
      var found = steps.find(function (s) {
        return Number(s.step_order || 0) === Number(stepOrder);
      });
      return found && found.name ? found.name : "Etapa " + stepOrder;
    },

    prependInitialDescriptionIfMissing(stepMap) {
      var text = (this.flowDescription(this.flow) || "").trim();
      if (!text) return;

      var step = this.ensureHistoryStep(stepMap, 1);
      var alreadyExists = step.items.some(function (item) {
        return item.id === "initial_description";
      });

      if (alreadyExists) return;

      step.items.unshift({
        id: "initial_description",
        type: "comment",
        text: text,
        user_name: this.openedByName(this.flow),
        created_at: this.flowCreatedAt(this.flow),
      });
    },

    buildHistoryByStep() {
      var stepMap = {};
      var transitionIdToStepOrder = {};
      var steps = this.flowTransitions(this.flow);

      steps.forEach(function (step) {
        if (step.id) {
          transitionIdToStepOrder[step.id] = step.step_order;
        }
      });

      this.prependInitialDescriptionIfMissing(stepMap);

      var comments = Array.isArray(this.flow && this.flow.comments)
        ? this.flow.comments
        : [];

      comments.forEach(
        function (comment) {
          var stepOrder = transitionIdToStepOrder[comment.transition_id] || 1;
          var bucket = this.ensureHistoryStep(stepMap, stepOrder);

          bucket.items.push({
            id: comment.id,
            type: "comment",
            text: comment.comment || comment.text || "",
            user_name:
              comment.user_name ||
              comment.created_by_name ||
              (comment.user && comment.user.name) ||
              "—",
            created_at: comment.created_at || null,
          });
        }.bind(this)
      );

      var files = this.flowFiles(this.flow);

      files.forEach(
        function (file) {
          var stepOrder = transitionIdToStepOrder[file.transition_id] || 1;
          var bucket = this.ensureHistoryStep(stepMap, stepOrder);

          bucket.items.push({
            id: file.id,
            type: "file",
            original_name: file.original_name,
            mime: file.mime,
            size: file.size,
            created_at: file.created_at || null,
            user_name:
              file.user_name ||
              file.uploaded_by_name ||
              (file.uploadedBy && file.uploadedBy.name) ||
              "—",
          });
        }.bind(this)
      );

      this.historyByStep = Object.values(stepMap)
        .filter(function (step) {
          return Array.isArray(step.items) && step.items.length;
        })
        .sort(function (a, b) {
          return Number(a.step_order || 0) - Number(b.step_order || 0);
        });
    },

    stepCompletedMeta(step) {
      var stepOrder = step && step.step_order ? step.step_order : 0;
      if (!stepOrder) return null;

      var historyStep = this.historyByStep.find(function (item) {
        return Number(item.step_order || 0) === Number(stepOrder);
      });

      if (
        !historyStep ||
        !Array.isArray(historyStep.items) ||
        !historyStep.items.length
      ) {
        return null;
      }

      var items = historyStep.items.slice().sort(function (a, b) {
        var da = a && a.created_at ? new Date(a.created_at).getTime() : 0;
        var db = b && b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });

      return items[0] || null;
    },

    stepDescriptions(step, flow) {
      var stepOrder = step && step.step_order ? Number(step.step_order) : 0;
      if (!stepOrder) return [];

      var list = [];

      var historyStep = this.historyByStep.find(function (item) {
        return Number(item.step_order || 0) === Number(stepOrder);
      });

      if (historyStep && Array.isArray(historyStep.items)) {
        historyStep.items
          .filter(function (item) {
            return item.type === "comment" && item.text;
          })
          .sort(function (a, b) {
            var da = a && a.created_at ? new Date(a.created_at).getTime() : 0;
            var db = b && b.created_at ? new Date(b.created_at).getTime() : 0;
            return da - db;
          })
          .forEach(function (item) {
            list.push({
              id: item.id,
              text: item.text,
              user_name: item.user_name || "—",
              created_at: item.created_at || null,
            });
          });
      }

      if (!list.length && Number(step.step_order) === 1) {
        var openingText = this.flowDescription(flow);
        if (openingText) {
          list.push({
            id: "opening_description",
            text: openingText,
            user_name: this.openedByName(flow),
            created_at: this.flowCreatedAt(flow),
          });
        }
      }

      return list;
    },

    formatDate(date) {
      if (!date) return "—";
      try {
        return new Date(date).toLocaleString("pt-BR");
      } catch (e) {
        return date;
      }
    },
  },
};
</script>

<style scoped>
.page-inprogress-view {
  background: #f6f8fb;
  min-height: 100%;
}

.page-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
}

.top-actions {
  margin-bottom: 16px;
}

.summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px;
  background: #ffffff;
  margin-bottom: 22px;
}

.summary-title-block {
  margin-bottom: 18px;
}

.summary-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
}

.summary-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.summary-tag {
  display: inline-flex;
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.summary-item {
  border-left: 1px solid #e5e7eb;
  padding-left: 14px;
}

.summary-item:first-child {
  border-left: 0;
  padding-left: 0;
}

.summary-item-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
}

.summary-mini {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.section-block {
  margin-top: 18px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.section-header h4 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.section-subtitle {
  font-size: 13px;
  color: #6b7280;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(260px, 1fr));
  gap: 16px;
}

.step-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  background: #ffffff;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 5px solid #d1d5db;
}

.step-card.done {
  border-left-color: #16a34a;
}

.step-card.current {
  border-left-color: #2563eb;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.08);
}

.step-card.pending {
  border-left-color: #cbd5e1;
}

.step-card.rejected {
  border-left-color: #dc2626;
}

.step-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.step-order {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.step-department {
  font-size: 13px;
  color: #6b7280;
}

.step-info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.step-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.step-description p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}

.description-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}

.description-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f9fafb;
}

.description-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.description-item p {
  margin: 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}

.step-approvals {
  margin-top: 4px;
}

.approval-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.approval-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f9fafb;
}

.approval-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.approval-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.approval-status.is-approved {
  background: #dcfce7;
  color: #15803d;
}

.approval-status.is-rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.approval-status.is-pending {
  background: #dbeafe;
  color: #1d4ed8;
}

.approval-date {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.approval-comment {
  margin-top: 4px;
  font-size: 12px;
  color: #374151;
}

.step-files ul {
  margin: 6px 0 0;
  padding-left: 18px;
}

.step-files li {
  font-size: 13px;
  color: #374151;
  margin-bottom: 4px;
}

.status-badge,
.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-badge.is-new {
  background: #eef2ff;
  color: #4338ca;
}

.status-badge.is-progress {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-badge.is-done {
  background: #dcfce7;
  color: #15803d;
}

.status-badge.is-rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.step-badge.done {
  background: #dcfce7;
  color: #15803d;
}

.step-badge.current {
  background: #dbeafe;
  color: #1d4ed8;
}

.step-badge.pending {
  background: #f3f4f6;
  color: #6b7280;
}

.step-badge.rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.empty-state {
  padding: 32px 12px;
  text-align: center;
  color: #6b7280;
}

.error-state {
  color: #b91c1c;
}

@media (max-width: 1400px) {
  .steps-grid {
    grid-template-columns: repeat(3, minmax(260px, 1fr));
  }
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .steps-grid {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }
}
</style>
